import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DocenteAsignaturas() {
  // Generar años desde 2020 hasta año actual + 1
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedTipo, setSelectedTipo] = useState('preadmision');
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(false);


  const timeSlots = [];
  for (let i = 6; i <= 18; i++) {
    timeSlots.push(`${i.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${i.toString().padStart(2, '0')}:30`);
  }

  const days = ['Horario', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
  const dayMap = {
    'lunes': 'lunes',
    'martes': 'martes',
    'miercoles': 'miercoles',
    'miércoles': 'miercoles',
    'jueves': 'jueves',
    'viernes': 'viernes'
  };

  useEffect(() => {
    loadHorarios();
  }, [selectedYear, selectedTipo]);

  const loadHorarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/docente/horarios?gestion=${selectedYear}&tipo=${selectedTipo}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setHorarios(data.horarios || []);
    } catch (error) {
      console.error('Error loading horarios:', error);
      setHorarios([]);
    } finally {
      setLoading(false);
    }
  };

  // Convertir horarios de BD a estructura de grilla
  const getScheduleGrid = () => {
    const grid = timeSlots.map(time => ({
      time,
      lunes: null,
      martes: null,
      miercoles: null,
      jueves: null,
      viernes: null
    }));

    horarios.forEach(h => {
      // Función para calcular slots ocupados
      const processEntry = (diaRaw, hIni, hFin, sala) => {
        const slots = hIni && hFin ? timeSlots.filter(t => t >= hIni && t < hFin) : [];
        if (slots.length === 0) return;

        const dia = dayMap[diaRaw?.toLowerCase()] || diaRaw?.toLowerCase();
        if (!dia || grid[0][dia] === undefined) return;

        const slotIndexInicio = timeSlots.indexOf(slots[0]);
        const numSlots = slots.length;

        for (let i = 0; i < numSlots && slotIndexInicio + i < grid.length; i++) {
          if (i === 0) {
            grid[slotIndexInicio][dia] = {
              code: h.codigo || '',
              subject: `${h.asignatura_nombre || 'Asignatura'} - ${h.paralelo || 'P'} - Sala ${sala || h.sala || '?'}`,
              color: h.color_docente || '#E5E7EB',
              rowSpan: numSlots,
              isStart: true
            };
          } else {
            grid[slotIndexInicio + i][dia] = { isContinuation: true };
          }
        }
      };

      if (h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0) {
        h.horarios_detalle.forEach(d => {
          processEntry(d.dia, d.hora_inicio?.substring(0, 5), d.hora_fin?.substring(0, 5), d.sala);
        });
      } else {
        const diasRaw = h.dia_semana?.split ? h.dia_semana.split(',') : (Array.isArray(h.dia_semana) ? h.dia_semana : []);
        diasRaw.forEach(diaRaw => {
          processEntry(diaRaw.trim(), h.hora_inicio?.substring(0, 5), h.hora_fin?.substring(0, 5), h.sala);
        });
      }
    });

    return grid;
  };

  const currentSchedule = getScheduleGrid();

  const renderCell = (cellData) => {
    if (!cellData) return null;

    const bgColor = cellData.color || '#E5E7EB';
    const isLightColor = bgColor.startsWith('#') &&
      parseInt(bgColor.slice(1), 16) > 0xffffff * 0.7; // Umbral de brillo ajustado
    const textColor = isLightColor ? '#374151' : '#FFFFFF';

    return (
      <div
        className="text-center w-full flex flex-col justify-center shadow-inner rounded-none bg-blue-500"
        style={{
          backgroundColor: bgColor,
          height: `${(cellData.rowSpan || 1) * 40}px`
        }}
      >
        <div className="font-bold text-[9px] md:text-[10px] leading-tight" style={{ color: textColor }}>{cellData.code}</div>
        <div className="text-[8px] md:text-[9px] leading-tight mt-0.5 line-clamp-2" style={{ color: textColor }}>{cellData.subject}</div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">Docente &gt; Inscripción Asignaturas</h1>
      </div>

      {/* Selector de Tipo y Año */}
      <div className="mb-6 md:mb-8 flex flex-wrap gap-3">
        <div className="relative inline-block">
          <select
            value={selectedTipo}
            onChange={(e) => setSelectedTipo(e.target.value)}
            className="appearance-none bg-white border border-gray-300 rounded-full px-4 md:px-6 py-1.5 md:py-2 pr-8 md:pr-10 text-sm md:text-base text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer">
            <option value="preadmision">Curso de Preadmisión</option>
            <option value="admision">Prueba de Admisión Académica</option>
            <option value="matriculacion">Licenciatura</option>
          </select>
          <ChevronDown
            className="absolute right-2 md:right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none"
            size={16} />
        </div>

        <div className="relative inline-block">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="appearance-none bg-white border border-gray-300 rounded-full px-4 md:px-6 py-1.5 md:py-2 pr-8 md:pr-10 text-sm md:text-base text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 md:right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none" size={16} />
        </div>
      </div>

      {/* Tabla de Horarios */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando horarios...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[600px]">
              <thead>
                <tr>
                  {days.map((day, index) => (
                    <th
                      key={day}
                      className={`border border-gray-300 py-2 px-2 md:py-3 md:px-4 text-center text-xs md:text-sm font-semibold text-gray-800 ${index === 0 ? 'bg-gray-100 min-w-[140px]' : 'bg-white min-w-[160px]'
                        }`}
                    >
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentSchedule.map((row, rowIndex) => {
                  // Función helper para renderizar celda de día
                  const renderDayCell = (cellData, dayKey) => {
                    // Si es continuación, no renderizar (será cubierta por rowSpan anterior)
                    if (cellData?.isContinuation) return null;

                    return (
                      <td
                        key={dayKey}
                        className="border border-gray-300 p-0 h-full"
                        rowSpan={cellData?.rowSpan || 1}
                      >
                        {renderCell(cellData)}
                      </td>
                    );
                  };

                  return (
                    <tr key={rowIndex} className="h-10">
                      <td className="border border-gray-200 py-1 px-1 md:py-1.5 md:px-2 text-center text-[10px] md:text-xs font-bold text-gray-500 bg-gray-50 whitespace-nowrap min-w-[140px]">
                        {(() => {
                          const start = row.time;
                          const [h, m] = start.split(':').map(Number);
                          const nextM = m === 30 ? 0 : 30;
                          const nextH = m === 30 ? h + 1 : h;
                          const end = `${nextH.toString().padStart(2, '0')}:${nextM.toString().padStart(2, '0')}`;
                          return `${start} - ${end}`;
                        })()}
                      </td>
                      {renderDayCell(row.lunes, 'lunes')}
                      {renderDayCell(row.martes, 'martes')}
                      {renderDayCell(row.miercoles, 'miercoles')}
                      {renderDayCell(row.jueves, 'jueves')}
                      {renderDayCell(row.viernes, 'viernes')}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && horarios.length === 0 && (
          <div className="p-4 text-center text-gray-500 text-sm">
            No hay horarios asignados para la gestión {selectedYear}
          </div>
        )}

        {/* Lista de asignaturas asignadas */}
        {!loading && horarios.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b">
              <h3 className="text-sm font-semibold text-gray-700">Asignaturas Asignadas ({horarios.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 border text-left">Código</th>
                    <th className="px-3 py-2 border text-left font-semibold">Asignatura</th>
                    <th className="px-3 py-2 border text-center font-semibold">Paralelo</th>
                    <th className="px-3 py-2 border text-left font-semibold">Días</th>
                    <th className="px-3 py-2 border text-center font-semibold">Horario</th>
                    <th className="px-3 py-2 border text-center font-semibold">Sala</th>
                    <th className="px-3 py-2 border text-left font-semibold">Carrera</th>
                    {selectedTipo === 'matriculacion' && (
                        <th className="px-3 py-2 text-left font-semibold">Mención</th>
                      )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {horarios.map((h, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 border font-medium">{h.codigo || '-'}</td>
                      <td className="px-3 py-2 border">{h.asignatura_nombre || '-'}</td>
                      <td className="px-3 py-2 border text-center">{h.paralelo || '-'}</td>
                      <td className="px-3 py-2 border">
                        {h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0
                          ? h.horarios_detalle.map((d, i) => <div key={i}>{d.dia}</div>)
                          : (h.dia_semana ? (Array.isArray(h.dia_semana) ? h.dia_semana.join(', ') : h.dia_semana) : <span className="text-gray-400 italic">Sin asignar</span>)
                        }
                      </td>
                      <td className="px-3 py-2 border text-center">
                        {h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0
                          ? h.horarios_detalle.map((d, i) => <div key={i}>{d.hora_inicio?.substring(0, 5)} - {d.hora_fin?.substring(0, 5)}</div>)
                          : (h.hora_inicio && h.hora_fin ? `${h.hora_inicio.substring(0, 5)} - ${h.hora_fin.substring(0, 5)}` : <span className="text-gray-400 italic">Sin asignar</span>)
                        }
                      </td>
                      <td className="px-3 py-2 border text-center">
                        {h.horarios_detalle && Array.isArray(h.horarios_detalle) && h.horarios_detalle.length > 0
                          ? h.horarios_detalle.map((d, i) => <div key={i}>{d.sala || '-'}</div>)
                          : (h.sala || <span className="text-gray-400 italic">-</span>)
                        }
                      </td>
                      {/* <td className="px-3 py-2">{h.carrera || h.mencion || '-'}</td> */}
                      <td className="px-3 py-2 border">{h.carrera || '-'}</td>

                      {selectedTipo === 'matriculacion' && (
                        <td className="px-3 py-2 border">{h.mencion || '-'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}